import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import WorkTemplateContext, {
  WorkTemplateActionTypes,
} from '@atom/components/workTemplate/WorkTemplateContext';
import { WORK_TEMPLATE_TASK_UPDATE } from '@atom/graph/workTemplate';
import { DatePicker } from '@atom/mui';
import { WorkTemplateTaskItem } from '@atom/types/workTemplate';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';

import classes from './workTemplateTaskDetail.module.css';

const DEBOUNCE_TIME = 1000;

const TaskDueDate = () => {
  const { workTemplate, task, dispatch } = useContext(WorkTemplateContext);

  const [dueDate, setDueDate] = useState<Date>(
    task.dueDate ? new Date(task.dueDate) : null,
  );

  const [taskTemplateUpdate] = useMutation(WORK_TEMPLATE_TASK_UPDATE);

  useEffect(() => {
    setDueDate(task.dueDate ? new Date(task.dueDate) : null);
  }, [task.dueDate]);

  const updateTaskProperty = (
    property: keyof WorkTemplateTaskItem,
    value: any,
  ) => {
    dispatch({
      type: WorkTemplateActionTypes.UPDATE_TASK_PROPERTY,
      data: {
        taskId: task.id,
        property,
        value,
      },
    });
  };

  const handleSubmit = async (date: Date) => {
    if (R.isEmpty(date) || R.isNil(date)) {
      return;
    }

    const res = await taskTemplateUpdate({
      variables: {
        input: {
          workTemplateId: workTemplate.id,
          taskId: task.id,
          dueDate: convertDateToMillisGMTMidday(date),
        },
      },
    });

    updateTaskProperty(
      'dueDate',
      res?.data?.workOrderTemplateTaskUpdate?.dueDate,
    );
  };

  const debouncedHandleSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_TIME),
    [task.id, workTemplate.id],
  );

  const onChange = (date?: Date) => {
    setDueDate(date);

    if (!date) {
      return;
    } else if (!Number.isNaN(date.valueOf())) {
      debouncedHandleSubmit(date);
    }
  };

  const isDisabled =
    workTemplate?.published || doesNotHaveRolePermissions(ROLE_SETS.ORG_ADMIN);

  return (
    <div className={clsx(classes.timeRowItem, classes.input)}>
      <div className={classes.label}>Due Date</div>
      <div className={classes.timePickerContainer}>
        <DatePicker value={dueDate} onChange={onChange} disabled={isDisabled} />
      </div>
    </div>
  );
};

export default TaskDueDate;
